/*
 * <td-bundle-reviews-panel> — Bundle Reviews panel.
 *
 * Server-renders page 1 from `judgeme.review_widget_data`; keeps
 * Judge.me's hidden widget mounted for sort + Load More. Histogram
 * filters hit JM's `reviews_for_widget` endpoint directly.
 */

if (!customElements.get("td-bundle-reviews-tabs")) {
  class TdBundleReviewsTabs extends HTMLElement {
    constructor() {
      super();
      this.tabs = [];
      this.panels = [];
      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleTabKeydown = this.handleTabKeydown.bind(this);
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll("[data-td-bundle-reviews-tab]"));
      this.panels = Array.from(this.querySelectorAll("[data-td-bundle-reviews-panel]"));

      if (this.tabs.length < 2 || this.panels.length < 2) {
        return;
      }

      this.tabs.forEach((tab) => {
        tab.addEventListener("click", this.handleTabClick);
        tab.addEventListener("keydown", this.handleTabKeydown);
      });
    }

    disconnectedCallback() {
      this.tabs.forEach((tab) => {
        tab.removeEventListener("click", this.handleTabClick);
        tab.removeEventListener("keydown", this.handleTabKeydown);
      });
    }

    handleTabClick(event) {
      this.activateTab(event.currentTarget);
    }

    handleTabKeydown(event) {
      const currentIndex = this.tabs.indexOf(event.currentTarget);
      if (currentIndex === -1) return;

      let nextIndex = currentIndex;
      if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        nextIndex = (currentIndex + 1) % this.tabs.length;
      } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      } else if (event.key === "Home") {
        nextIndex = 0;
      } else if (event.key === "End") {
        nextIndex = this.tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      this.activateTab(this.tabs[nextIndex], true);
    }

    activateTab(nextTab, focus = false) {
      const nextHandle = nextTab.dataset.tdBundleReviewsTab;
      this.tabs.forEach((tab) => {
        const isActive = tab === nextTab;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
        tab.setAttribute("tabindex", isActive ? "0" : "-1");
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.tdBundleReviewsPanel !== nextHandle;
      });

      if (focus) {
        nextTab.focus();
      }
    }
  }

  customElements.define("td-bundle-reviews-tabs", TdBundleReviewsTabs);
}

if (!customElements.get("td-bundle-reviews-panel")) {
  class TdBundleReviews extends HTMLElement {
    static jmBaseUrl = null;
    static _hooked = false;

    /*
     * Monkeypatch `fetch` and `XMLHttpRequest.open` to sniff Judge.me's
     * `reviews_for_widget` URL the first time JM's bundled JS calls it.
     * We need that URL (with shop/widget identifiers baked in) to power our
     * own histogram-filter requests, and JM does not expose it as a public
     * API. The wrappers pass every call through to the original unchanged.
     * Runs at class-evaluation time so the hook is in place before JM fires.
     */
    static {
      if (!TdBundleReviews._hooked) {
        TdBundleReviews._hooked = true;
        const origFetch = window.fetch;
        if (origFetch) {
          window.fetch = function (input) {
            try {
              const u = typeof input === "string" ? input : (input && input.url) || "";
              if (/reviews_for_widget/.test(u)) TdBundleReviews.jmBaseUrl = u;
            } catch (_) {}
            return origFetch.apply(this, arguments);
          };
        }
        const origOpen = XMLHttpRequest.prototype.open;
        XMLHttpRequest.prototype.open = function (method, url) {
          try {
            if (url && /reviews_for_widget/.test(url)) TdBundleReviews.jmBaseUrl = url;
          } catch (_) {}
          return origOpen.apply(this, arguments);
        };
      }
    }

    static PHOTO_WIDTHS = [140, 240, 360];
    static PHOTO_SIZES_ATTR = "(max-width: 835px) 69px, 120px";
    static SVG_VERIFIED =
      '<svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">' +
      '<path d="M23 11.99l-2.44-2.79.34-3.69-3.61-.82-1.89-3.2L12 2.96 8.6 1.49 6.71 4.69 3.1 5.5l.34 3.7L1 11.99l2.44 2.79-.34 3.7 3.61.82L6.71 19.3 8.6 22.51 12 21.04l3.4 1.47 1.89-3.21 3.61-.82-.34-3.7L23 11.99zm-12.91 4.72l-3.8-3.81 1.48-1.48 2.32 2.33 5.85-5.87 1.48 1.48-7.33 7.35z" fill="currentColor"/>' +
      "</svg>";

    static imgixVariant(url, w) {
      if (!url) return "";
      if (!/imgix\.net/.test(url)) return url;
      const base = url.split("?")[0];
      return base + "?auto=format,compress&q=70&w=" + w;
    }

    static imgixSrcset(url) {
      if (!url || !/imgix\.net/.test(url)) return "";
      return TdBundleReviews.PHOTO_WIDTHS.map(
        (w) => TdBundleReviews.imgixVariant(url, w) + " " + w + "w"
      ).join(", ");
    }

    static applyPhotoAttrs(img, url) {
      img.src = TdBundleReviews.imgixVariant(url, 240) || url;
      const srcset = TdBundleReviews.imgixSrcset(url);
      if (srcset) {
        img.setAttribute("srcset", srcset);
        img.setAttribute("sizes", TdBundleReviews.PHOTO_SIZES_ATTR);
      }
      img.alt = "";
      img.loading = "lazy";
      img.decoding = "async";
      img.setAttribute("fetchpriority", "low");
      img.width = 240;
      img.height = 240;
    }

    static decodeHtmlEntities(str) {
      if (!str) return "";
      const t = document.createElement("textarea");
      t.innerHTML = str;
      return t.value;
    }

    constructor() {
      super();
      this.activeRating = null;
      this.defaultSort = this.dataset.defaultSort || "";
      this.defaultSortApplied = false;
      this.filteredPage = 0;
      this.sortPending = false;
      this.sortBefore = "";
      this.sortFallback = null;
      this.sortDebounce = null;
      this.resizeRaf = 0;
      this.observerPending = false;

      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleAnchorClick = this.handleAnchorClick.bind(this);
    }

    connectedCallback() {
      this.list = this.querySelector(".td-bundle-reviews__list");
      this.empty = this.querySelector(".td-bundle-reviews__empty");
      this.loadMoreBtn = this.querySelector(".td-bundle-reviews__view-all");
      this.shell = this.querySelector(".td-bundle-reviews__judgeme-shell");
      this.histogramRows = this.querySelectorAll(".td-bundle-reviews__breakdown-row");

      this.appendMissingCards();
      this.updateEmptyState();
      this.syncLoadMoreVisibility();
      this.schedulePhotoSync();
      this.checkAllClamping();

      this.wireHistogram();
      this.wireLoadMore();
      this.wireDelegation();
      this.wireResize();
      this.wireShellObserver();
      this.applyDefaultSort();
      document.addEventListener("click", this.handleAnchorClick);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      window.removeEventListener("resize", this.handleResize);
      document.removeEventListener("click", this.handleAnchorClick);
    }

    extractReviewFromJmCard(jmCard) {
      const data = {};
      const titleEl = jmCard.querySelector(".jm-review-content__title");
      data.title = titleEl ? titleEl.textContent.trim() : "";

      const bodyEl = jmCard.querySelector(".jm-review-content__body");
      data.bodyHTML = bodyEl ? bodyEl.innerHTML.trim() : "";

      const nameEl = jmCard.querySelector(".jm-reviewer-info__name");
      data.reviewer = nameEl ? nameEl.textContent.trim() : "Anonymous";

      data.verified = !!jmCard.querySelector(".jm-verified-buyer-badge");

      const starsEl = jmCard.querySelector(".jm-star-rating");
      let rating = 5;
      if (starsEl) {
        const m = (starsEl.getAttribute("aria-label") || "").match(/^(\d+(?:\.\d+)?)/);
        if (m) rating = parseFloat(m[1]);
      }
      data.rating = rating;

      let dateText = "";
      const details = jmCard.querySelector(".jm-reviewer-info__details");
      if (details) {
        for (const t of details.querySelectorAll(".jm-text")) {
          const v = t.textContent.trim();
          if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) { dateText = v; break; }
        }
      }
      data.date = dateText;

      const photoEls = jmCard.querySelectorAll(
        ".jm-media-thumbnail-list__thumbnail img"
      );
      data.photoUrls = Array.from(photoEls).map((el) => el.src).filter(Boolean);
      data.photoUrl = data.photoUrls[0] || "";

      return data;
    }

    buildCustomCard(data) {
      const li = document.createElement("li");
      li.className = "td-bundle-review";

      const sidebar = document.createElement("div");
      sidebar.className = "td-bundle-review__sidebar";
      if (data.photoUrl) {
        const photoBtn = document.createElement("button");
        photoBtn.type = "button";
        photoBtn.className = "td-bundle-review__photo";
        const all = (data.photoUrls && data.photoUrls.length
          ? data.photoUrls
          : [data.photoUrl]).filter(Boolean);
        photoBtn.dataset.photos = all.join("||");
        photoBtn.setAttribute(
          "aria-label",
          all.length > 1 ? `View ${all.length} review photos` : "View review photo"
        );
        const img = document.createElement("img");
        img.className = "td-bundle-review__photo-img";
        TdBundleReviews.applyPhotoAttrs(img, data.photoUrl);
        photoBtn.appendChild(img);
        sidebar.appendChild(photoBtn);
      }
      const sidebarDate = document.createElement("p");
      sidebarDate.className = "td-bundle-review__date td-bundle-review__date--sidebar";
      sidebarDate.textContent = data.date;
      sidebar.appendChild(sidebarDate);
      if (data.verified) {
        const sidebarVerified = document.createElement("span");
        sidebarVerified.className =
          "td-bundle-review__verified td-bundle-review__verified--sidebar";
        sidebarVerified.innerHTML = TdBundleReviews.SVG_VERIFIED + "<span>Verified</span>";
        sidebar.appendChild(sidebarVerified);
      }
      li.appendChild(sidebar);

      const content = document.createElement("div");
      content.className = "td-bundle-review__content";

      const title = document.createElement("p");
      title.className = "td-bundle-review__title";
      title.textContent = TdBundleReviews.decodeHtmlEntities(data.title);
      content.appendChild(title);

      const inlineDate = document.createElement("p");
      inlineDate.className = "td-bundle-review__date td-bundle-review__date--inline";
      inlineDate.textContent = data.date;
      content.appendChild(inlineDate);

      const meta = document.createElement("div");
      meta.className = "td-bundle-review__meta";

      const starPct = Math.max(0, Math.min(100, (data.rating || 0) * 20));
      const stars = document.createElement("span");
      stars.className = "td-bundle-review__stars";
      stars.setAttribute("aria-label", data.rating + " out of 5 stars");
      stars.innerHTML =
        '<span class="td-bundle-review__stars-bg">★★★★★</span>' +
        '<span class="td-bundle-review__stars-fg" style="width: ' + starPct + '%">★★★★★</span>';
      meta.appendChild(stars);

      const reviewer = document.createElement("span");
      reviewer.className = "td-bundle-review__reviewer";
      reviewer.textContent = "- " + TdBundleReviews.decodeHtmlEntities(data.reviewer || "Anonymous");
      meta.appendChild(reviewer);

      if (data.verified) {
        const inlineVerified = document.createElement("span");
        inlineVerified.className =
          "td-bundle-review__verified td-bundle-review__verified--inline";
        inlineVerified.innerHTML = TdBundleReviews.SVG_VERIFIED + "<span>Verified</span>";
        meta.appendChild(inlineVerified);
      }

      content.appendChild(meta);

      if (data.bodyHTML) {
        const body = document.createElement("div");
        body.className = "td-bundle-review__body";
        body.innerHTML = data.bodyHTML;
        content.appendChild(body);

        const more = document.createElement("button");
        more.className = "td-bundle-review__read-more";
        more.type = "button";
        more.setAttribute("aria-expanded", "false");
        more.innerHTML =
          '<span class="td-bundle-review__read-more-more">Read more</span>' +
          '<span class="td-bundle-review__read-more-less">See less</span>';
        content.appendChild(more);
      }

      li.appendChild(content);
      return li;
    }

    buildCustomCardFromApi(review) {
      if (!review) return null;
      const created = review.created_at || "";
      let date = "";
      if (created.length >= 10) {
        date = created.slice(5, 7) + "/" + created.slice(8, 10) + "/" + created.slice(0, 4);
      }

      const photoUrls = (review.pictures_urls || [])
        .map((pic) => {
          if (typeof pic === "string") return pic;
          if (typeof pic === "object" && pic) {
            return (
              pic.huge || pic.original || pic.compact || pic.small ||
              pic.huge_url || pic.original_url || pic.compact_url || pic.thumb_url || ""
            );
          }
          return "";
        })
        .filter(Boolean);

      return this.buildCustomCard({
        title: review.title || "",
        bodyHTML: review.body_html || (review.body ? "<p>" + review.body + "</p>" : ""),
        reviewer: review.reviewer_name || "Anonymous",
        verified: !!review.verified_buyer,
        rating: review.rating || 5,
        date: date,
        photoUrl: photoUrls[0] || "",
        photoUrls: photoUrls,
      });
    }

    appendMissingCards() {
      if (!this.list) return;
      const jmCards = this.querySelectorAll(
        ".td-bundle-reviews__judgeme-shell .jm-review-item"
      );
      const customCount = this.list.querySelectorAll(".td-bundle-review").length;
      let appended = false;
      for (let i = customCount; i < jmCards.length; i++) {
        const data = this.extractReviewFromJmCard(jmCards[i]);
        if (!data.title && !data.bodyHTML) continue;
        this.list.appendChild(this.buildCustomCard(data));
        appended = true;
      }
      if (appended) this.schedulePhotoSync();
      this.updateEmptyState();
      this.checkAllClamping();
    }

    rebuildAllCards() {
      if (!this.list) return;
      const jmCards = this.querySelectorAll(
        ".td-bundle-reviews__judgeme-shell .jm-review-item"
      );
      while (this.list.firstChild) this.list.removeChild(this.list.firstChild);
      for (const c of jmCards) {
        const data = this.extractReviewFromJmCard(c);
        if (!data.title && !data.bodyHTML) continue;
        this.list.appendChild(this.buildCustomCard(data));
      }
      this.updateEmptyState();
      this.checkAllClamping();
    }

    renderReviewsArray(reviews) {
      if (!this.list) return;
      while (this.list.firstChild) this.list.removeChild(this.list.firstChild);
      for (const r of reviews) {
        const card = this.buildCustomCardFromApi(r);
        if (card) this.list.appendChild(card);
      }
      this.updateEmptyState();
      this.checkAllClamping();
    }

    updateEmptyState() {
      if (!this.list || !this.empty) return false;
      const count = this.list.querySelectorAll(".td-bundle-review").length;
      if (count === 0) {
        this.list.setAttribute("hidden", "");
        this.empty.removeAttribute("hidden");
        if (this.loadMoreBtn) this.loadMoreBtn.style.display = "none";
        return true;
      }
      this.list.removeAttribute("hidden");
      this.empty.setAttribute("hidden", "");
      return false;
    }

    syncLoadMoreVisibility() {
      if (!this.loadMoreBtn) return;
      if (this.loadMoreBtn.classList.contains("td-bundle-reviews__view-all--loading")) return;
      if (this.list && this.list.querySelectorAll(".td-bundle-review").length === 0) {
        this.loadMoreBtn.style.display = "none";
        return;
      }
      const jmBtn = this.querySelector(
        ".jm-load-more-pagination button, .jm-pagination-controls button"
      );
      if (!jmBtn || jmBtn.disabled || jmBtn.getAttribute("aria-disabled") === "true") {
        this.loadMoreBtn.style.display = "none";
      } else {
        this.loadMoreBtn.style.display = "";
      }
    }

    setLoading(isLoading) {
      if (!this.loadMoreBtn) return;
      this.loadMoreBtn.classList.toggle("td-bundle-reviews__view-all--loading", isLoading);
      if (isLoading) this.loadMoreBtn.setAttribute("aria-busy", "true");
      else this.loadMoreBtn.removeAttribute("aria-busy");
    }

    wireLoadMore() {
      if (!this.loadMoreBtn) return;
      this.loadMoreBtn.addEventListener("click", (e) => this.handleLoadMoreClick(e));
    }

    handleLoadMoreClick(event) {
      event.preventDefault();

      if (this.activeRating) {
        const nextPage = (this.filteredPage || 1) + 1;
        const sortValue = this.getActiveSortValue();
        this.setLoading(true);
        this.fetchFilteredReviews(this.activeRating, nextPage, sortValue)
          .then((data) => {
            this.filteredPage = nextPage;
            const reviews = (data && data.reviews) || [];
            for (const r of reviews) {
              const card = this.buildCustomCardFromApi(r);
              if (card) this.list.appendChild(card);
            }
            this.setLoading(false);
            this.updateEmptyState();
            this.checkAllClamping();
            if (data && data.pagination && data.pagination.current_page >= data.pagination.total_pages) {
              if (this.loadMoreBtn) this.loadMoreBtn.style.display = "none";
            }
            this.schedulePhotoSync();
          })
          .catch(() => this.setLoading(false));
        return;
      }

      const jmBtn = this.querySelector(
        ".jm-load-more-pagination button, .jm-pagination-controls button"
      );
      if (!jmBtn) return;

      const beforeCount = this.list.querySelectorAll(".td-bundle-review").length;
      this.setLoading(true);
      jmBtn.click();

      let checks = 0;
      const poller = setInterval(() => {
        checks++;
        const nowCount = this.list.querySelectorAll(".td-bundle-review").length;
        if (nowCount > beforeCount || checks >= 40) { // 40×200ms = 8s cap
          clearInterval(poller);
          this.setLoading(false);
          this.syncLoadMoreVisibility();
        }
      }, 200);
    }

    getActiveSortValue() {
      const sel = this.querySelector(".jm-sort-filter select");
      return sel ? sel.value : "";
    }

    applyDefaultSort() {
      if (!this.defaultSort || this.defaultSortApplied) return;
      const sel = this.querySelector(".jm-sort-filter select");
      if (!sel) return;

      const hasMatchingOption = Array.from(sel.options || []).some(
        (option) => option.value === this.defaultSort
      );

      if (!hasMatchingOption) {
        this.defaultSortApplied = true;
        return;
      }

      this.defaultSortApplied = true;

      if (sel.value === this.defaultSort) {
        return;
      }

      sel.value = this.defaultSort;
      sel.dispatchEvent(new Event("change", { bubbles: true }));
    }

    getJmTitlesSignature() {
      const titles = this.querySelectorAll(
        ".td-bundle-reviews__judgeme-shell .jm-review-content__title"
      );
      let out = "";
      for (const t of titles) out += "|" + t.textContent;
      return out;
    }

    handleSortChange() {
      if (this.activeRating) { this.applyRatingFilter(this.activeRating); return; }

      this.setLoading(true);
      if (this.list) while (this.list.firstChild) this.list.removeChild(this.list.firstChild);
      this.sortPending = true;
      this.sortBefore = this.getJmTitlesSignature();
      if (this.sortFallback) clearTimeout(this.sortFallback);
      this.sortFallback = setTimeout(() => this.forceResolveSort(), 3000);
    }

    tryResolveSort() {
      if (!this.sortPending) return;
      const nowSig = this.getJmTitlesSignature();
      if (nowSig && nowSig !== this.sortBefore) this.forceResolveSort();
    }

    forceResolveSort() {
      if (!this.sortPending) return;
      this.sortPending = false;
      if (this.sortFallback) { clearTimeout(this.sortFallback); this.sortFallback = null; }
      if (this.sortDebounce) { clearTimeout(this.sortDebounce); this.sortDebounce = null; }
      this.rebuildAllCards();
      this.setLoading(false);
      this.syncLoadMoreVisibility();
      this.schedulePhotoSync();
    }

    syncPhotosFromJm() {
      if (this.activeRating) return; // Filter mode: cards come from API.
      const customCards = this.querySelectorAll(".td-bundle-reviews__list .td-bundle-review");
      const jmCards = this.querySelectorAll(".td-bundle-reviews__judgeme-shell .jm-review-item");
      for (let i = 0; i < customCards.length && i < jmCards.length; i++) {
        if (customCards[i].querySelector(".td-bundle-review__photo")) continue;
        const jmThumbs = jmCards[i].querySelectorAll(
          ".jm-media-thumbnail-list__thumbnail img"
        );
        const all = Array.from(jmThumbs).map((el) => el.src).filter(Boolean);
        if (all.length === 0) continue;
        const sidebar = customCards[i].querySelector(".td-bundle-review__sidebar");
        if (!sidebar) continue;
        const photoBtn = document.createElement("button");
        photoBtn.type = "button";
        photoBtn.className = "td-bundle-review__photo";
        photoBtn.dataset.photos = all.join("||");
        photoBtn.setAttribute(
          "aria-label",
          all.length > 1 ? `View ${all.length} review photos` : "View review photo"
        );
        const img = document.createElement("img");
        img.className = "td-bundle-review__photo-img";
        TdBundleReviews.applyPhotoAttrs(img, all[0]);
        photoBtn.appendChild(img);
        sidebar.insertBefore(photoBtn, sidebar.firstChild);
      }
    }

    schedulePhotoSync() {
      [200, 500, 1000, 2000].forEach((d) => setTimeout(() => this.syncPhotosFromJm(), d));
    }

    fetchReviewsPage({ rating, sortBy, page, perPage } = {}) {
      const base = TdBundleReviews.jmBaseUrl;
      if (!base) return Promise.reject(new Error("Judge.me base URL not captured yet"));
      let url = base
        .replace(/&page=[^&]*/g, "")
        .replace(/&per_page=[^&]*/g, "")
        .replace(/&filter_rating=[^&]*/g, "")
        .replace(/&sort_by=[^&]*/g, "")
        .replace(/&sort_dir=[^&]*/g, "")
        .replace(/&ts=[^&]*/g, "");
      url += "&page=" + (page || 1);
      if (perPage) url += "&per_page=" + perPage;
      if (rating != null) url += "&filter_rating=" + rating;
      if (sortBy) {
        url += "&sort_by=" + encodeURIComponent(sortBy);
        if (sortBy === "lowest_rating") url += "&sort_dir=asc";
      }
      url += "&ts=" + encodeURIComponent(new Date().toISOString());
      return fetch(url, { credentials: "omit" }).then((r) => r.json());
    }

    fetchFilteredReviews(rating, page, sortValue) {
      return this.fetchReviewsPage({ rating, page, sortBy: sortValue });
    }

    extractPhotoUrls(review) {
      return (review.pictures_urls || [])
        .map((pic) => {
          if (typeof pic === "string") return pic;
          if (typeof pic === "object" && pic) {
            return (
              pic.huge || pic.original || pic.compact || pic.small ||
              pic.huge_url || pic.original_url || pic.compact_url || pic.thumb_url || ""
            );
          }
          return "";
        })
        .filter(Boolean);
    }

    setActiveHistogramRow(rating) {
      this.histogramRows.forEach((row) => {
        const r = parseInt(row.getAttribute("data-rating"), 10);
        row.classList.toggle(
          "td-bundle-reviews__breakdown-row--active",
          rating != null && r === rating
        );
      });
    }

    applyRatingFilter(rating) {
      this.setLoading(true);
      this.activeRating = rating;
      this.filteredPage = 1;
      this.setActiveHistogramRow(rating);

      let attempts = 0;
      const tryFetch = () => {
        attempts++;
        if (!TdBundleReviews.jmBaseUrl && attempts < 30) {
          if (attempts === 1) {
            const sel = this.querySelector(".jm-sort-filter select");
            if (sel) sel.dispatchEvent(new Event("change", { bubbles: true }));
          }
          setTimeout(tryFetch, 200);
          return;
        }
        if (!TdBundleReviews.jmBaseUrl) { this.setLoading(false); return; }
        this.fetchFilteredReviews(rating, 1, this.getActiveSortValue())
          .then((data) => {
            this.renderReviewsArray((data && data.reviews) || []);
            this.setLoading(false);
            if (data && data.pagination && data.pagination.current_page >= data.pagination.total_pages) {
              if (this.loadMoreBtn) this.loadMoreBtn.style.display = "none";
            } else {
              this.syncLoadMoreVisibility();
            }
          })
          .catch(() => this.setLoading(false));
      };
      tryFetch();
    }

    clearRatingFilter() {
      this.setActiveHistogramRow(null);
      this.activeRating = null;
      this.filteredPage = 0;
      this.rebuildAllCards();
      this.syncLoadMoreVisibility();
    }

    wireHistogram() {
      this.histogramRows.forEach((row) => {
        row.addEventListener("click", () => {
          const rating = parseInt(row.getAttribute("data-rating"), 10);
          if (!rating) return;
          if (this.activeRating === rating) this.clearRatingFilter();
          else this.applyRatingFilter(rating);
        });
      });
    }

    checkClamping(card) {
      const body = card.querySelector(".td-bundle-review__body");
      if (!body) return;
      if (card.classList.contains("td-bundle-review--expanded")) return;
      const ch = body.clientHeight, sh = body.scrollHeight;
      if (ch === 0 || sh === 0) {
        requestAnimationFrame(() => this.checkClamping(card));
        return;
      }
      card.classList.toggle("td-bundle-review--needs-clamp", sh > ch + 1);
    }

    checkAllClamping() {
      for (const c of this.querySelectorAll(".td-bundle-review")) this.checkClamping(c);
    }

    wireDelegation() {
      this.addEventListener("click", this.handleClick);
      this.addEventListener("change", this.handleChange, true);
    }

    handleClick(e) {
      if (!e.target.closest) return;
      const readMoreBtn = e.target.closest(".td-bundle-review__read-more");
      if (readMoreBtn) {
        const card = readMoreBtn.closest(".td-bundle-review");
        if (!card) return;
        const expanded = card.classList.toggle("td-bundle-review--expanded");
        readMoreBtn.setAttribute("aria-expanded", expanded ? "true" : "false");
        return;
      }
      const photoBtn = e.target.closest(".td-bundle-review__photo");
      if (photoBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.forwardToJmLightbox(photoBtn);
      }
    }

    forwardToJmLightbox(photoBtn) {
      const first = (photoBtn.dataset.photos || "").split("||")[0];
      if (!first) return;
      const clickedBase = first.split("?")[0];
      const thumbs = document.querySelectorAll(".jdgm-gallery__thumbnail-link");
      for (const link of thumbs) {
        const img = link.querySelector(".jdgm-gallery__thumbnail");
        const mfpSrc = img?.getAttribute("data-mfp-src") || img?.src || "";
        if (mfpSrc.split("?")[0] === clickedBase) {
          setTimeout(() => link.click(), 0);
          return;
        }
      }
    }

    handleChange(e) {
      if (e.target && e.target.matches &&
          e.target.matches(".jm-sort-filter select, .jdgm-sort-dropdown")) {
        this.handleSortChange();
      }
    }

    wireResize() {
      window.addEventListener("resize", this.handleResize);
    }

    handleResize() {
      if (this.resizeRaf) return;
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = 0;
        this.checkAllClamping();
      });
    }

    handleAnchorClick(e) {
      if (!e.target.closest) return;
      const a = e.target.closest("a.td-bundle-rating__link, a[href*='#shopify-product-reviews']");
      if (!a) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button > 0) return;
      e.preventDefault();
      this.scrollIntoView({ behavior: "smooth", block: "start" });
      if (history.replaceState) {
        history.replaceState(null, "", "#shopify-product-reviews");
      }
    }

    wireShellObserver() {
      if (!this.shell) return;
      this.observer = new MutationObserver(() => {
        if (this.sortPending) { this.tryResolveSort(); return; }
        if (this.activeRating) return;
        if (this.observerPending) return;
        this.observerPending = true;
        setTimeout(() => {
          this.observerPending = false;
          this.applyDefaultSort();
          this.appendMissingCards();
          this.syncLoadMoreVisibility();
        }, 100);
      });
      this.observer.observe(this.shell, { childList: true, subtree: true });
    }
  }

  customElements.define("td-bundle-reviews-panel", TdBundleReviews);
}
